/**
 * Manual positional audio. A `PannerNode` per voice would give us HRTF at the
 * cost of a convolution per sound; at the distances this game cares about
 * (a thud four rooms away) what actually sells direction is the pan, the
 * distance roll-off and — most of all — how much top end has been eaten on the
 * way. That is three cheap parameters, so we compute them ourselves.
 */

import { TUNING } from '../core/config';
import { clamp, clamp01 } from '../core/math';

/** Where the player's head is and which way it faces. */
export interface Listener {
  x: number;
  z: number;
  /** Unit vector pointing to the listener's right. */
  rx: number;
  rz: number;
}

/** Per-voice spatial parameters. Reused; never allocated per call. */
export interface Spatial {
  /** Distance attenuation, 0..1. */
  gain: number;
  /** Stereo position, -1..1. */
  pan: number;
  /** Low-pass cutoff in Hz standing in for air and wall absorption. */
  cutoff: number;
  /** Multiplier on a sound's base reverb send. Far things are wetter. */
  sendScale: number;
  dist: number;
}

export function makeListener(): Listener {
  return { x: 0, z: 0, rx: 1, rz: 0 };
}

export function makeSpatial(): Spatial {
  return { gain: 1, pan: 0, cutoff: 20000, sendScale: 1, dist: 0 };
}

/** yaw = 0 looks down -Z, so the right vector is (cos yaw, sin yaw). */
export function setListener(l: Listener, x: number, z: number, yaw: number): void {
  l.x = x;
  l.z = z;
  l.rx = Math.cos(yaw);
  l.rz = Math.sin(yaw);
}

/** Metres beyond which a positional sound is not worth starting at all. */
export const MAX_AUDIBLE = TUNING.audio.maxAudibleDistance;

export function evalSpatial(l: Listener, x: number, z: number, out: Spatial): Spatial {
  const dx = x - l.x;
  const dz = z - l.z;
  const d = Math.hypot(dx, dz);
  out.dist = d;

  const ref = TUNING.audio.refDistance;
  const t = clamp01(d / MAX_AUDIBLE);
  // Inverse-distance law, windowed so it reaches exactly zero at the audible
  // limit instead of leaving an inaudible-but-alive voice running forever.
  out.gain = (ref / Math.max(ref, d)) * (1 - t);

  let pan = 0;
  let front = 1;
  if (d > 1e-3) {
    const nx = dx / d;
    const nz = dz / d;
    pan = clamp(nx * l.rx + nz * l.rz, -1, 1);
    // Forward is the right vector turned a quarter turn: (rz, -rx).
    front = nx * l.rz + nz * -l.rx;
    // A sound at arm's length should not slam into one ear.
    pan *= clamp01(d / 1.6) * 0.92;
  }
  out.pan = pan;

  // Roughly 18kHz at the listener down to 1kHz at the limit, logarithmically.
  // Behind the head, darker still — the only front/back cue stereo can give.
  const behind = 0.62 + 0.38 * clamp01((front + 1) * 0.5);
  out.cutoff = 18000 * Math.pow(0.055, t) * behind;

  // Distance is mostly communicated by the dry/wet balance, not by level.
  out.sendScale = 0.8 + 2.3 * t;
  return out;
}
